import React, { memo, useEffect, useRef, useState } from "react";
import cx from "classnames";
import { IonLabel } from "@ionic/react";
import {
  TimeRemaining,
  calculateTimeRemaining,
  formatWithLeadingZero,
} from "../../utils/common";
import s from "./WLCountdown.module.scss";

export type WLCountdownVariant = "default" | "secondsOnlyRed";

export interface WLCountdownProps {
  title: string;
  expiresInDt?: number;
  /** Full duration with seconds in red + blinking animation. Ref: AppUser PowerUp GiveawayCountdown */
  variant?: WLCountdownVariant;
}

const resolveEndTimeMs = (expiresInDt: number | undefined): number => {
  if (expiresInDt != null && Number.isFinite(expiresInDt)) {
    return expiresInDt;
  }
  return Date.now();
};

const WLCountdown = ({
  title,
  expiresInDt,
  variant = "default",
}: WLCountdownProps) => {
  const countdownResetKey =
    expiresInDt != null && Number.isFinite(expiresInDt) ? expiresInDt : 0;

  const endTimeRef = useRef<number>(resolveEndTimeMs(expiresInDt));
  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining | null>(() =>
    calculateTimeRemaining(endTimeRef.current),
  );
  const timerIdRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    const endTime = resolveEndTimeMs(expiresInDt);
    endTimeRef.current = endTime;
    setTimeRemaining(calculateTimeRemaining(endTime));

    const id = setInterval(() => {
      const next = calculateTimeRemaining(endTimeRef.current);
      setTimeRemaining(next);
      const isExpired = Object.values(next).every((v) => v <= 0);
      if (isExpired && timerIdRef.current !== null) {
        clearInterval(timerIdRef.current);
        timerIdRef.current = null;
      }
    }, 1000);
    timerIdRef.current = id;

    return () => {
      if (timerIdRef.current !== null) {
        clearInterval(timerIdRef.current);
        timerIdRef.current = null;
      }
    };
  }, [countdownResetKey]);

  const displayTime = timeRemaining !== null ? timeRemaining : null;
  const hasNegative =
    displayTime !== null && Object.values(displayTime).some((v) => v < 0);
  const safeTime: TimeRemaining =
    displayTime !== null && hasNegative
      ? { days: 0, hrs: 0, mins: 0, secs: 0 }
      : (displayTime ?? { days: 0, hrs: 0, mins: 0, secs: 0 });

  const useRedBlinkSeconds = variant === "secondsOnlyRed";
  const timeEntries = Object.entries(safeTime) as [
    keyof TimeRemaining,
    number,
  ][];

  return (
    <div className={s.container}>
      <IonLabel className="win-sub winnersans medium white">{title}</IonLabel>
      <div className={s.countdown}>
        {timeEntries.map(([timeKey, timeValue]) => (
          <React.Fragment key={timeKey}>
            <div className={s.timeUnit}>
              <IonLabel className="win-extraSmall winnersans medium blue300-extended">
                {timeKey}
              </IonLabel>
              <IonLabel
                className={cx(
                  "win-header-4 gilroy semibold",
                  timeKey === "secs" &&
                    useRedBlinkSeconds &&
                    s.secondsOnlyRedNumber,
                  timeKey === "secs" &&
                    !useRedBlinkSeconds &&
                    (timeValue > 0 ? "secondary-500" : "blue300-extended"),
                  timeKey !== "secs" &&
                    (useRedBlinkSeconds
                      ? "blue300-extended"
                      : timeValue > 0
                        ? "secondary-500"
                        : "blue300-extended"),
                )}
              >
                {formatWithLeadingZero(timeValue)}
              </IonLabel>
            </div>
            {timeKey !== "secs" && (
              <div className="win-header-4 gilroy bold blue300-extended">:</div>
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default memo(WLCountdown);
